/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { Fragment, useEffect, useRef, type FunctionComponent } from 'react'

import { EuiCallOut, EuiLink, EuiSpacer } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import type { BudgetConfigurationValues } from '../../types'

type Props = {
  amountRef: React.RefObject<HTMLInputElement>
  cloudResourceRef: React.RefObject<HTMLDivElement>
  errors: Partial<Record<keyof BudgetConfigurationValues, string>>
  nameRef: React.RefObject<HTMLInputElement>
  scopeRef: React.RefObject<HTMLDivElement>
}

const ValidationErrorsCallout: FunctionComponent<Props> = ({
  amountRef,
  cloudResourceRef,
  errors,
  nameRef,
  scopeRef,
}) => {
  const calloutRef = useRef(null)

  const linkRefs = {
    amount: amountRef,
    name: nameRef,
    scopeType: scopeRef,
    scopeValues: cloudResourceRef,
  } as Record<keyof BudgetConfigurationValues, React.RefObject<HTMLElement>>

  useEffect(() => {
    scrollToElement(calloutRef)
  }, [])

  const scrollToElement = (ref: React.RefObject<HTMLElement | HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    ref.current?.focus()
  }

  return (
    <Fragment>
      <EuiCallOut
        data-test-subj='billing.notifications.budgets.configuration.validation-errors.callout'
        ref={calloutRef}
        announceOnMount={true}
        title={
          <FormattedMessage
            id='billing.notifications.budgets.configuration.validation-errors.title'
            defaultMessage='You must fix the following'
          />
        }
        color='danger'
        iconType='alert'
      >
        <ul>
          {Object.keys(errors).map((errorName) => (
            <li key={errorName}>
              <EuiLink color='danger' onClick={() => scrollToElement(linkRefs[errorName])}>
                {errors[errorName]}
              </EuiLink>
            </li>
          ))}
        </ul>
      </EuiCallOut>
      <EuiSpacer size='m' />
    </Fragment>
  )
}

export default ValidationErrorsCallout
